import { useId, useState, useRef, useLayoutEffect, type KeyboardEvent } from 'react';
import { useStore } from '@/store';

export interface TooltipProps {
  /** Screen-reader-friendly label for the trigger (defaults to "More information"). */
  label?: string;
  /** Tooltip body. Keep to ≤ 2 sentences per Stage 16 content rules. */
  children: React.ReactNode;
  /** Optional "Learn more" link text + destination screen. */
  learnMore?: { label: string; screen: string };
}

/**
 * Minimal inline help icon (`?`) with a hover/focus popover.
 * Keyboard accessible: focusable, Escape dismisses, described via aria-describedby.
 * No external library (Stage 16 / In-app Documentation).
 */
export function Tooltip({ label = 'More information', children, learnMore }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const tipId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const setActiveScreen = useStore((s) => s.setActiveScreen);
  // Re-position on open so the popover never overflows the viewport.
  useLayoutEffect(() => {
    if (!open) return;
    const bubble = bubbleRef.current;
    const trigger = triggerRef.current;
    if (!bubble || !trigger) return;
    const { left, width } = bubble.getBoundingClientRect();
    const margin = 8;
    if (left < margin) {
      const shift = margin - left;
      bubble.style.transform = `translateX(${shift}px)`;
    } else if (left + width > window.innerWidth - margin) {
      const shift = window.innerWidth - margin - (left + width);
      bubble.style.transform = `translateX(${shift}px)`;
    }
  }, [open]);

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      triggerRef.current?.blur();
    }
  }

  return (
    <span className="tooltip-host">
      <button
        ref={triggerRef}
        type="button"
        className="tooltip-trigger"
        aria-label={label}
        aria-describedby={tipId}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKeyDown}
      >
        <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false">
          <circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" strokeWidth="1" />
          <path d="M4.4 5a1.6 1.6 0 1 1 2.1 1.52c-.55.16-.7.38-.7.78" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
          <circle cx="6" cy="9.1" r="0.55" fill="currentColor" />
        </svg>
      </button>
      <span
        ref={bubbleRef}
        id={tipId}
        role="tooltip"
        className={`tooltip-bubble${open ? ' open' : ''}`}
      >
        {children}
        {learnMore ? (
          <a
            href={`#nav/${learnMore.screen}`}
            className="tooltip-learn-more"
            onClick={(e) => {
              e.preventDefault();
              setActiveScreen(learnMore.screen as never);
              setOpen(false);
              triggerRef.current?.blur();
            }}
          >
            {learnMore.label} →
          </a>
        ) : null}
      </span>
    </span>
  );
}
